import { datadogRum } from '@datadog/browser-rum'
import { PdfKitSource } from '@harvey-frontend/openapi/models/PdfKitSource'
import { UploadedFile } from '@harvey-frontend/openapi/models/UploadedFile'
import * as Sentry from '@sentry/react'

import {
  DialogStatus,
  DialogRequestType,
  TaskpaneResponseType,
  FILE_PREVIEWER_URL,
} from '../../../utils/constants'
import { ReferenceWithFile } from '../../file-previewer/utils/general-store'
import { SetHighlightProps } from '../stores/app-store'

interface GetSelectionProps {
  callback: ({ highlightedText }: SetHighlightProps) => void
}

const getSelection = async ({ callback }: GetSelectionProps) => {
  await Word.run(async (context) => {
    const range = context.document.getSelection()
    range.load('text')
    range.track()
    await context.sync()
    callback({ highlightedText: range })
  })
}

export interface TextWithReferences {
  text: string
  references: PdfKitSource[]
}

type EventHandlerCallbackType =
  | {
      message: string
      origin: string | undefined
    }
  | {
      error: number
    }

export const openFilePreviewer = async (
  dialogRef: React.MutableRefObject<Office.Dialog | null>,
  closeDialog: () => void,
  getAccessToken: () => Promise<string | null>,
  textWithReferences: TextWithReferences[],
  uploadedFiles: UploadedFile[],
  selectedReference: ReferenceWithFile | null = null
) => {
  closeDialog()
  const accessToken = await getAccessToken()
  Office.context.ui.displayDialogAsync(
    FILE_PREVIEWER_URL,
    { width: 75, height: 75 },
    (asyncResult: Office.AsyncResult<Office.Dialog>) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        console.error('Failed to display dialog', asyncResult.error.message)
        return
      }
      const dialog = asyncResult.value
      dialogRef.current = dialog
      dialog.addEventHandler(
        Office.EventType.DialogEventReceived,
        (event: EventHandlerCallbackType) => {
          if ('error' in event) {
            dialog.close()
            dialogRef.current = null
          }
        }
      )
      dialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        (event: EventHandlerCallbackType) => {
          if (!('message' in event)) {
            return
          }
          const message = event.message
          const parsedMessage = JSON.parse(message)
          const status = parsedMessage.status
          const requestType = parsedMessage.requestType
          if (status === DialogStatus.success) {
            const message = {
              type: TaskpaneResponseType.accessToken,
              data: { accessToken: accessToken },
            }
            dialog.messageChild(JSON.stringify(message))
          } else if (requestType === DialogRequestType.suggestions) {
            const message = {
              type: TaskpaneResponseType.textWithReferences,
              data: {
                textWithReferences,
                selectedReference,
                uploadedFiles,
              },
            }
            dialog.messageChild(JSON.stringify(message))
          }
        }
      )
    }
  )
}

/**
 * Gets a persistent custom property from the document if it exists.
 * @param key Key of the custom property.
 * @returns Value of the custom property, or null if the property does not exist.
 */
const getCustomProperty = async (key: string) => {
  return await Word.run(async (context) => {
    const customProperty =
      context.document.properties.customProperties.getItemOrNullObject(key)
    customProperty.load('value')
    await context.sync()
    return customProperty.isNullObject ? null : customProperty.value
  })
}

/**
 * Sets a persistent custom property on the document.
 * @param key Key of the custom property.
 * @param value Value of the custom property. Note that this must be <255 characters.
 */
const setCustomProperty = async (key: string, value: string) => {
  await Word.run(async (context) => {
    context.document.properties.customProperties.add(key, value)
    await context.sync()
  })
}

const getFileUrl = async () => {
  return new Promise((resolve: (value: string) => void, reject) => {
    Office.context.document.getFilePropertiesAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        const fileUrl = asyncResult.value.url
        resolve(fileUrl)
      } else {
        reject('Error retrieving file properties: ' + asyncResult.error.message)
      }
    })
  })
}

export const navigateDialog = (
  url: string,
  callback: (arg: DialogStatus, parsedAccessToken?: string) => void
) => {
  Office.context.ui.displayDialogAsync(
    url,
    { height: 60, width: 30 },
    function (asyncResult: Office.AsyncResult<Office.Dialog>) {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        callback(DialogStatus.error)
      } else {
        const dialog = asyncResult.value
        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (event: EventHandlerCallbackType) => {
            if ('error' in event) {
              const error = event.error
              if (error) callback(DialogStatus.error)
            }
          }
        )
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (event: EventHandlerCallbackType) => {
            if (!('message' in event)) {
              return
            }
            const message = event.message
            const parsedMessage = JSON.parse(message || '{}') as {
              accessToken?: string
              status?: DialogStatus
            }
            const parsedAccessToken = parsedMessage.accessToken || null
            dialog.close()
            if (parsedAccessToken) {
              callback(DialogStatus.success, parsedAccessToken)
            } else {
              callback(DialogStatus.error)
            }
          }
        )
      }
    }
  )
}

const getSliceAsync = (
  file: Office.File,
  nextSlice: number,
  sliceCount: number,
  docDataSlices: number[][],
  slicesReceived: number,
  callback: (blob: Blob) => void,
  errorCallback?: (error: Error) => void
) => {
  file.getSliceAsync(nextSlice, (sliceResult) => {
    if (sliceResult.status == Office.AsyncResultStatus.Failed) {
      const error = new Error(
        `Error getting slice: ${sliceResult.error.message}`
      )
      console.error(error)
      Sentry.captureException(error)
      if (errorCallback) {
        errorCallback(error)
      }
      return
    }

    docDataSlices[sliceResult.value.index] = sliceResult.value.data
    if (++slicesReceived == sliceCount) {
      onGotAllSlices(docDataSlices, callback)
    } else {
      getSliceAsync(
        file,
        ++nextSlice,
        sliceCount,
        docDataSlices,
        slicesReceived,
        callback,
        errorCallback
      )
    }
  })
}

const onGotAllSlices = (
  docDataSlices: number[][],
  callback: (blob: Blob) => void
) => {
  const totalLength = docDataSlices.reduce(
    (acc, slice) => acc + slice.length,
    0
  )

  const docData = new Uint8Array(totalLength)
  let offset = 0
  for (const slice of docDataSlices) {
    docData.set(slice, offset)
    offset += slice.length
  }

  callback(new Blob([docData], { type: 'application/pdf' }))
}

/**
 * Converts the document to a PDF asynchronously.
 * @param callback Callback function to do something with the PDF blob.
 */
export const getDocumentAsPdfAsync = async (callback: (blob: Blob) => void) => {
  return new Promise<void>((resolve, reject) => {
    Office.context.document.getFileAsync(Office.FileType.Pdf, (result) => {
      if (result.status == Office.AsyncResultStatus.Failed) {
        console.error('Error converting to pdf:', result.error.message)
        Sentry.captureException(result.error)
        reject(result.error)
      }

      try {
        const myFile = result.value
        const sliceCount = myFile.sliceCount
        const docDataSlices: number[][] = []
        const slicesReceived = 0
        getSliceAsync(
          myFile,
          0,
          sliceCount,
          docDataSlices,
          slicesReceived,
          (blob) => {
            callback(blob)
            resolve()
          },
          (error) => {
            reject(error)
          }
        )
      } catch (error) {
        reject(error)
      } finally {
        if (result.value) {
          result.value.closeAsync()
        }
      }
    })
  })
}

/**
 * Searches for the text in the document and selects it.
 * @param text Text to search for. NOTE: This has a max length of 255 characters.
 * If the text is longer than 255 characters, it will be truncated.
 * Throws an error if the text is not found.
 */
export const jumpToTextInDocument = async (text: string) => {
  return await Word.run(async (context) => {
    datadogRum.startDurationVital('wordAddInJumpToTextInDocument')
    let searchText = text.slice(0, 255).trim()

    // Algo: first, search for an exact match. If not found,
    // trim words off the front of the string until we find a match.
    // This helps because the references from OCR often includes section numbers
    // that are not searchable.
    while (searchText.length > 0) {
      const searchMatches = context.document.search(searchText, {
        ignorePunct: true,
        ignoreSpace: true,
      })
      const searchMatch = searchMatches.getFirstOrNullObject()
      // eslint-disable-next-line office-addins/no-context-sync-in-loop
      await context.sync()

      if (!searchMatch.isNullObject) {
        searchMatches.track()
        searchMatch.track()
        searchMatch.load('text')
        // eslint-disable-next-line office-addins/no-context-sync-in-loop
        await context.sync()
        datadogRum.stopDurationVital('wordAddInJumpToTextInDocument')
        return searchMatch
      }

      const firstSpaceIdx = searchText.indexOf(' ')
      searchText =
        firstSpaceIdx === -1
          ? ''
          : searchText.substring(firstSpaceIdx + 1).trim()
    }
    datadogRum.stopDurationVital('wordAddInJumpToTextInDocument')
    throw new Error('Text not found in document')
  })
}

export { getCustomProperty, getFileUrl, getSelection, setCustomProperty }
